import { browser } from '$app/environment';

import type { ErrorStatusMapping } from '$shared/lib/errorHandlers/index';
import { ApplicationEvent } from '$shared/constants/ApplicationEvent';
import application from '$shared/lib/events/application';
import BaseService from '$shared/lib/services/BaseService';
import { sentry } from '$shared/helpers/logger/sentry';
import type User from '$shared/models/User';
import { ServiceException } from '$shared/exceptions/ServiceException';
import ServiceErrorWrapper from '$shared/helpers/decorators/ServiceErrorWrapper';
import glabalAuthData from '$shared/lib/api/glabalAuthData';
import userService from './userService';
import type { IPasswordAuth } from '$shared/interfaces/api/IPasswordAuth';
import type { IGoogleAuth } from '$shared/interfaces/api/IGoogleAuth';
import type { IForgotPassword } from '$shared/interfaces/api/IForgotPassword';
import type { IResetPassword } from '$shared/interfaces/api/IResetPassword';
import { api } from '$shared/api/index';
import Authorization from '$shared/models/Authorization';
import UserRole from '$shared/models/UserRole';
import UIErrorMessage from '$shared/constants/UIErrorMessage';
import { AuthType } from '$shared/constants/AuthType';
import type AuthToken from '$shared/models/api/AuthToken';
import { Module } from '$shared/constants/Module';
import type { IAuthorization } from '$shared/interfaces/api/IAuthorization';
import type { IPageRes } from '$shared/interfaces/api/IPageRes';
import sessionExpireService from './sessionExpireService';
import type CredOrg from '$shared/models/CredOrg';
import type { IOAuthModel } from '$shared/interfaces/api/IOAuthModel';
import { userStore } from '$shared/stores/user';
import { authStore } from '$shared/stores/authStore';
import { authData } from '$shared/lib/data/AuthData';

const authErrors: ErrorStatusMapping = {
  400: {
    title: UIErrorMessage.AuthFailed
  },
  403: {
    title: UIErrorMessage.AuthForbidden
  },
  409: {
    title: UIErrorMessage.PasswordReset
  }
};

const passwordAuthErrors: ErrorStatusMapping = {
  ...authErrors,
  403: {
    title: UIErrorMessage.InvalidCredential
  }
};
delete passwordAuthErrors['400'];

const ConsoleLocalStorageKey = 'verifier-console-user';
const TimeToLive = '30:00:00:00';
const CredTimeToLive = '00:12:00:00';
const SsoAuthLocalStorageKey = 'sso-auth';

class AuthService extends BaseService {
  private authorization: Authorization | null = Authorization.create({} as IAuthorization);
  private oneTimeToken: AuthToken | null = null;

  constructor() {
    super();
  }

  public init(): void {
    try {
      let authorization: Authorization | null = null;
      browser && (authorization = Authorization.restore());
      if (!authorization) return;

      // If the auth is cred, then the user should select the org, then only we can init
      // the dispatch event on athurised, it would avoid unncessary api calls.
      const noDispatch = !!(authorization.isCredUser && !authorization.selectedCredOrg);
      this.onAuthorization(authorization, noDispatch);
    } catch {
      this.onUserUnauthorized();
    }
  }

  public get isAuthorised(): boolean {
    return !!this.authorization?.isAuthorised;
  }

  public get isCredAuthType(): boolean {
    return !!this.authorization?.isCredUser;
  }

  public get isAdmin(): boolean {
    return this.getUserRole().hasWritePermission(Module.Providers);
  }

  public get selectedCredOrg(): string {
    return this.authorization?.selectedCredOrg || '';
  }

  public get userRole(): UserRole | null {
    return this.authorization?.role || null;
  }

  public get isOneTimeUser(): boolean {
    return Authorization.isOneTimeUser();
  }

  public get isNoCredOrgSelected(): boolean {
    return this.isCredAuthType && !this.selectedCredOrg;
  }

  public get isSsoAuth(): boolean {
    return !!localStorage.getItem(SsoAuthLocalStorageKey);
  }

  getUserEmail(): string {
    return this.authorization?.email || '';
  }

  getUserName(): string {
    return this.authorization?.name || '';
  }

  getUserRole(): UserRole {
    return this.userRole || new UserRole(null, null, null);
  }

  getAuthToken(): string {
    return this.authorization?.token || '';
  }

  getTempTokenExpireDateTime(): string | null {
    return this.oneTimeToken?.expiresAt?.getLocalizedDateAndTime() || null;
  }

  getEmbedTokenExpiresAt(): number | null {
    try {
      const token = this.getAuthToken();
      const segment = token.split('.')[1];
      if (!segment) return null;
      const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded));
      return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  getCredUserInfo(): User | null {
    return (
      userStore.credUsers.find(
        (user) => user.email?.toLowerCase() === this.getUserEmail()?.toLowerCase()
      ) || null
    );
  }

  @ServiceErrorWrapper()
  public async changeCredOrganisationAsync(orgId: string): Promise<void> {
    if (!orgId)
      throw new Error('`Organization ID` is required to call the function changeCredOrganisation');

    glabalAuthData.setOrgId(orgId);
    this.authorization?.setSelectedOrg(orgId);
    application.dispatchEvent(ApplicationEvent.OnUserOrgChanged);
    this.init();
    await this.setCredOrgUserRoleAsync(orgId);
  }

  @ServiceErrorWrapper()
  async changePasswordAsync(authInfo: IPasswordAuth): Promise<void> {
    authInfo.timeToLive = TimeToLive;
    const authorization: Authorization = await api.changePasswordAsync(authInfo);
    authorization.setUserAuthType(this.authorization?.userAuthType || null);
    authorization.selectedCredOrg = this.authorization?.selectedCredOrg || '';
    this.onAuthorization(authorization);
  }

  @ServiceErrorWrapper()
  setOneTimeToken(token: string): void {
    if (!token) {
      throw new Error('One-time token required to authenticate user');
    }
    this.invalidateToken();
    application.dispatchEvent(ApplicationEvent.OnUnauthorized);
    this.onAuthorization(Authorization.createOneTimeUser(token));
  }

  @ServiceErrorWrapper()
  async setEmbedToken(
    token: string,
    organizationId?: string,
    email?: string,
    name?: string
  ): Promise<void> {
    if (!token) throw new Error('Embed token required');
    await this.invalidateToken();
    application.dispatchEvent(ApplicationEvent.OnUnauthorized);
    if (organizationId) {
      glabalAuthData.setOrgId(organizationId);
    }
    this.onAuthorization(Authorization.createOneTimeUser(token, email, name));
  }

  @ServiceErrorWrapper()
  async passwordAuthenticationAsync(authInfo: IPasswordAuth): Promise<void> {
    authInfo.timeToLive = TimeToLive;
    const authorization: Authorization = await api.authPasswordAsync(authInfo);
    this.onAuthorization(authorization);
  }

  @ServiceErrorWrapper()
  async googleAuthenticationAsync(authInfo: IGoogleAuth): Promise<void> {
    authInfo.timeToLive = TimeToLive;
    const authorization: Authorization = await api.authGoogleAsync(authInfo);
    this.onAuthorization(authorization);
  }

  @ServiceErrorWrapper()
  async oAuthAuthenticationAsync(accessToken: string): Promise<void> {
    const authInfo: IOAuthModel = {
      accessToken
    };
    const authorization: Authorization = await api.authOAuthAsync(authInfo);
    this.onAuthorization(authorization);
    localStorage.setItem(SsoAuthLocalStorageKey, 'true');
  }

  @ServiceErrorWrapper()
  async credLoginAsync(token: string): Promise<void> {
    const authInfo: IGoogleAuth = {
      token,
      timeToLive: CredTimeToLive
    };
    const authorization: Authorization = await api.credAuthGoogleAsync(authInfo);
    authorization.setUserAuthType(AuthType.Cred);
    this.onAuthorization(authorization, true);
  }

  @ServiceErrorWrapper()
  async consoleLoginAsync(token: string): Promise<void> {
    const authInfo: IGoogleAuth = {
      token,
      timeToLive: CredTimeToLive
    };
    const authorization: Authorization = await api.consoleAuthGoogleAsync(authInfo);
    localStorage.setItem(ConsoleLocalStorageKey, JSON.stringify(authorization.toResponse()));
  }

  async forgotPasswordAsync(authInfo: IForgotPassword): Promise<void> {
    await api.forgotPasswordAsync(authInfo);
  }

  async resetPasswordAsync(authInfo: IResetPassword): Promise<void> {
    await api.resetPasswordAsync(authInfo);
  }

  async invalidateAccessTokensAsync(tokenIdList: string[]): Promise<void> {
    await Promise.all(tokenIdList.map((tokenId) => api.invalidateAccessTokenAsync(tokenId)));
  }

  async getTokensAsync(): Promise<Array<AuthToken>> {
    const result: IPageRes<AuthToken> = await api.getTokensAsync();
    return result?.items;
  }

  @ServiceErrorWrapper()
  async validateOneTimeToken(): Promise<boolean> {
    if (this.oneTimeToken) {
      return true;
    }
    const tokens: AuthToken[] = await this.getTokensAsync();
    const currentToken = tokens?.find((token) => token.isCurrent);
    this.oneTimeToken = currentToken || null;
    return !!currentToken;
  }

  @ServiceErrorWrapper()
  logout(): void {
    if (this.isOneTimeUser) {
      Authorization.clean();
    } else {
      this.invalidateToken();
    }
    application.dispatchEvent(ApplicationEvent.OnUnauthorized);
  }

  @ServiceErrorWrapper()
  private async invalidateToken(): Promise<void> {
    try {
      const tokenId = this.authorization?.tokenId;
      const tempAuthentication = Authorization.restore();
      if (!tokenId || !tempAuthentication) {
        return;
      }
      if (this.isCredAuthType) {
        await api.invalidateCredAccessTokenAsync(tokenId);
      } else {
        await api.invalidateAccessTokenAsync(tokenId);
      }
    } catch (err: any) {
      if (!(err instanceof Error)) return;
      sentry.warn('Application failed on User logout', {
        error: err,
        email: this.authorization?.email
      });
    }
  }

  //#region  Handle application Events
  override onUserUnauthorized(): void {
    glabalAuthData.clearAccessToken();
    glabalAuthData.clearOrgId();
    sessionExpireService.clearTimer();
    Authorization.clean();
    this.authorization = null;
    authData.reset();
    this.reset();
  }

  override onUserOrgChanged(): void {
    return; // Nothing to do when org changed
  }
  //#endregion

  private reset(): void {
    localStorage.removeItem(SsoAuthLocalStorageKey);
  }

  private onAuthorization(authorization: Authorization, noDispatch = false): void {
    glabalAuthData.setAccessToken(authorization.token);
    this.authorization = authorization;
    authData.setAuthData(authorization);

    // Setting the selected org id to api layer for adding header
    this.isCredAuthType && this.selectedCredOrg && glabalAuthData.setOrgId(this.selectedCredOrg);
    this.authorization.store();
    if (authorization) sessionExpireService.init(authorization);
    if (!noDispatch) {
      application.dispatchEvent(ApplicationEvent.OnAuthorized);
    }
  }

  private async setCredOrgUserRoleAsync(orgId: string): Promise<void> {
    const orgUser = authStore.credOrgs?.find((credOrg: CredOrg) => credOrg.id === orgId);
    if (!orgUser) {
      const exception = ServiceException.from(
        'AuthService',
        'setCredOrgUserRole',
        { email: this.getUserEmail(), orgId },
        new Error('Cred User selected organization is not found'),
        {}
      );
      sentry.log(exception);
    }
    const userRole: UserRole | undefined = await userService.getUserRoleByRoleAsync(orgUser?.role);
    this.authorization?.setCredUserRole(userRole);
  }
}

export default new AuthService();
